import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'

import Table from 'components/Table'

import WistiaVideoRow from './WistiaVideoRow'
import Spinner from 'components/Spinner'
import HelpResourceRow from './HelpResourceRow'
import ThermostatReleaseRow from './ThermostatReleaseRow'
import Resource, { ValidResourceTypes } from 'types/Resource'

type ResourceProps = {
  type: ValidResourceTypes
  resource: Resource
}

type Props = {
  type: ValidResourceTypes
  resources: Array<Resource>
  isLoaded: boolean
  onSuccessfulSubmission: () => void
  onClickEditResource:(val: ResourceProps)=>void
}

const ResourceTable = ({ type, resources, isLoaded, onSuccessfulSubmission, onClickEditResource }: Props) => {
  const [nameSortDirection, setNameSortDirection] = useState<'asc' | 'desc' | null>('desc')
  const [dateSortDirection, setDateSortDirection] = useState<'asc' | 'desc' | null>(null)
  const [sortedResources, setSortedResources] = useState<Array<Resource>>(resources)

  useEffect(() => {
    setSortedResources(resources)
  }, [resources])

  let content
  let hasThermostatSpecificHeaders = false

  const resourceTableSetProps = {
    onSuccessfulSubmission,
    onClickEditResource
  }

  switch (type) {
    case 'video':
      content = isLoaded ? sortedResources.map((v) => (<WistiaVideoRow resource={v} key={v.hashedId} {...resourceTableSetProps} />)) : null
      break
    case 'article':
      content = isLoaded ? sortedResources.map((h) => (<HelpResourceRow resource={h} key={h.s3Key} type="article" {...resourceTableSetProps} />)) : null
      break
    case 'faq':
      content = isLoaded ? sortedResources.map((faq) => <HelpResourceRow resource={faq} key={faq.s3Key} type="faq" {...resourceTableSetProps} />) : null
      break
    case 'thermostat':
      content = isLoaded ? resources.map((thermostat) => <ThermostatReleaseRow resource={thermostat} key={thermostat.id} type="thermostat" {...resourceTableSetProps} />) : null
      hasThermostatSpecificHeaders = true
      break
    default:
      content = null
  }

  const onClickNameSortIcon = useCallback(() => {
    setNameSortDirection(nameSortDirection === 'asc' ? 'desc' : 'asc')

    const resourcesSortedByName = resources.sort((a, b) => {
      if (nameSortDirection === 'asc') {
        return a.name.localeCompare(b.name)
      } else {
        return b.name.localeCompare(a.name)
      }
    })

    setSortedResources(resourcesSortedByName)
  }, [nameSortDirection, resources])

  const onClickDateSortIcon = useCallback(() => {
    setNameSortDirection(null)
    setDateSortDirection(dateSortDirection === 'asc' ? 'desc' : 'asc')

    const resourcesSortedByDate = resources.sort((a, b) => {
      const dateA = new Date((a.wistiaUpdatedAt || a.createdAt) ?? 0).getTime()
      const dateB = new Date((b.wistiaUpdatedAt || b.createdAt) ?? 0).getTime()

      if (dateSortDirection === 'asc') {
        return dateA - dateB
      } else {
        return dateB - dateA
      }
    })

    setSortedResources(resourcesSortedByDate)
  }, [dateSortDirection, resources])

  return (
    <Table>
      <Table.Header>
        {hasThermostatSpecificHeaders && (
          <>
            <S.VersionHeaderCell title="Version" />
            <S.ModelHeaderCell title="Model(s)" />
            <S.DescriptionHeaderCell title="Description" />
            <S.DateHeaderCell title="Release Date" />
            <S.PublishedStatusHeaderCell title="Published Status" />
          </>
        )}

        {!hasThermostatSpecificHeaders && (
          <>
            <S.NameHeaderCell
              title="Name"
              sortable
              sortDirection={nameSortDirection}
              onSort={onClickNameSortIcon}
            />

            <S.DescriptionHeaderCell title="Description" />

            <S.DateHeaderCell
              title="Date"
              sortable
              sortDirection={dateSortDirection}
              onSort={onClickDateSortIcon}
            />

            <S.PublishedStatusHeaderCell title="Published Status" />
          </>
        )}

      </Table.Header>

      {(!isLoaded) && <Spinner variant="page" />}
      {content}
    </Table>
  )
}

export default ResourceTable

const S = {
  DateHeaderCell: styled(Table.HeaderCell)`
    flex: 1;
  `,
  ModelHeaderCell: styled(Table.HeaderCell)`
    flex: 1;
  `,
  VersionHeaderCell: styled(Table.HeaderCell)`
    flex: 1;
  `,
  DescriptionHeaderCell: styled(Table.HeaderCell)`
    flex: 2;
  `,
  NameHeaderCell: styled(Table.HeaderCell)`
    flex: 2;
    min-width: 229px;
  `,
  PublishedStatusHeaderCell: styled(Table.HeaderCell)`
    flex: 1;
  `
}
